/** Cursor encoding and context fingerprinting for focused facet pagination. */

import { FacetRequest, FilterDefinition } from './schema';
import { normalizeFilterClauses, normalizeSearchQuery } from './filters';
import { sha256 } from '@noble/hashes/sha2';
import { bytesToHex } from '@noble/hashes/utils';
import { fromByteArray, toByteArray } from 'base64-js';

function stringToUtf8Bytes(value: string): Uint8Array {
  const encoded = encodeURIComponent(value);
  const bytes: number[] = [];
  for (let index = 0; index < encoded.length; index += 1) {
    if (encoded[index] === '%') {
      bytes.push(Number.parseInt(encoded.slice(index + 1, index + 3), 16));
      index += 2;
    } else {
      bytes.push(encoded.charCodeAt(index));
    }
  }
  return Uint8Array.from(bytes);
}

function utf8BytesToString(bytes: Uint8Array): string {
  return decodeURIComponent(
    [...bytes].map(byte => `%${byte.toString(16).padStart(2, '0')}`).join(''),
  );
}

/**
 * Opaque cursor payload bound to normalized request context
 */
export interface FacetCursorPayload {
  version: number;
  /** Absent for keyword cursors; required for semantic cursors by request validation. */
  semanticSession?: string;
  facetId: string;
  afterDisplay: string; // case-folded display value
  afterValue: string; // raw value for tie-breaking
  issuedAt: number; // timestamp in ms
  expiresAt: number; // timestamp in ms
  requestFingerprint: string; // SHA-256 hex of canonical context
}

/**
 * Canonical normalized facet context used for fingerprinting and execution
 */
export interface CanonicalFacetContext {
  environment: string;
  query: string; // normalized
  filters: CanonicalFilterClause[]; // sorted by ID/operator, values normalized
  facetId?: string; // optional, for focused requests
  limit: number; // effective limit after applying default
  filterFingerprint: string; // SHA-256 of material filter semantics
  requestFingerprint: string; // SHA-256 of entire context + config
}

/**
 * Canonical filter clause with normalized values and material metadata
 */
export interface CanonicalFilterClause {
  id: string; // public filter ID
  field: string; // allowlisted indexed path
  operator: string; // validated operator
  values: unknown[]; // normalized, deduplicated, sorted
  facetable: boolean; // whether this field may be selected as a facet
}

/**
 * Encodes a cursor as a versioned base64url string
 * Payload includes everything needed to validate and continue
 * @param payload The cursor state to encode
 * @returns base64url-encoded opaque string
 */
export function encodeFacetCursor(payload: FacetCursorPayload): string {
  const json = JSON.stringify(payload);
  return fromByteArray(stringToUtf8Bytes(json))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

/**
 * Decodes a cursor and validates its structure (not expiry/fingerprint)
 * @param cursor The opaque cursor string
 * @returns Parsed payload or throws if malformed
 */
export function decodeFacetCursor(cursor: string): FacetCursorPayload {
  try {
    if (!/^[A-Za-z0-9_-]+$/.test(cursor)) {
      throw new Error('Invalid cursor encoding');
    }
    const base64 = cursor.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const json = utf8BytesToString(toByteArray(padded));
    const payload = JSON.parse(json) as FacetCursorPayload;

    // Validate required fields
    if (
      typeof payload.version !== 'number' ||
      typeof payload.facetId !== 'string' ||
      typeof payload.afterDisplay !== 'string' ||
      typeof payload.afterValue !== 'string' ||
      typeof payload.issuedAt !== 'number' ||
      typeof payload.expiresAt !== 'number' ||
      typeof payload.requestFingerprint !== 'string' ||
      (payload.semanticSession !== undefined &&
        typeof payload.semanticSession !== 'string')
    ) {
      throw new Error('Invalid cursor payload structure');
    }

    return payload;
  } catch (error) {
    throw new Error(
      `Failed to decode cursor: ${
        error instanceof Error ? error.message : String(error)
      }`,
    );
  }
}

/**
 * Creates a cursor payload for a facet value page
 * The returned payload encodes the ordering key and binds to the canonical context
 * @param facetId The focused facet ID
 * @param lastDisplayValue Case-folded display value of last returned group
 * @param lastRawValue Raw value of last returned group for tie-breaking
 * @param context The canonical facet context
 * @param ttlMs Time-to-live in milliseconds
 * @returns Cursor payload ready for encoding
 */
export function createFacetCursorPayload(
  facetId: string,
  lastDisplayValue: string,
  lastRawValue: string,
  context: CanonicalFacetContext,
  ttlMs: number,
): FacetCursorPayload {
  const issuedAt = Date.now();
  return {
    version: 1,
    facetId,
    afterDisplay: lastDisplayValue,
    afterValue: lastRawValue,
    issuedAt,
    expiresAt: issuedAt + ttlMs,
    requestFingerprint: context.requestFingerprint,
  };
}

/**
 * Validates a cursor payload against current request context
 * @param payload The decoded cursor
 * @param context The current canonical context
 * @returns true if valid, throws with specific error type otherwise
 */
export function validateFacetCursor(
  payload: FacetCursorPayload,
  context: CanonicalFacetContext,
  now = Date.now(),
): boolean {
  // Check version
  if (payload.version !== 1) {
    throw new Error('Cursor version unsupported');
  }

  // Check expiry
  if (payload.expiresAt < now) {
    throw new Error('Cursor expired');
  }

  // Check focused facet matches
  if (!context.facetId || payload.facetId !== context.facetId) {
    throw new Error('Cursor facet mismatch');
  }

  // Check request fingerprint matches
  if (payload.requestFingerprint !== context.requestFingerprint) {
    throw new Error('Cursor request mismatch');
  }

  return true;
}

/**
 * Computes a SHA-256 fingerprint.
 * @param data The data to fingerprint
 * @returns hex-encoded SHA-256
 */
export async function computeFingerprint(data: string): Promise<string> {
  return bytesToHex(sha256(stringToUtf8Bytes(data)));
}

/**
 * Computes the canonical context fingerprint from a facet request and config
 * Binds the cursor to the exact normalized state
 * @param request The validated facet request
 * @param config The current filter configuration snapshot
 * @returns The full canonical context including fingerprints
 */
export async function computeCanonicalContext(
  request: FacetRequest,
  config: {
    filters: FilterDefinition[];
    executions: Array<{ id: string; field: string }>;
    filterFingerprint: string;
  },
): Promise<CanonicalFacetContext> {
  const limit = request.limit ?? 100;
  const normalized = normalizeFilterClauses(request.filters, config.filters);
  if (!normalized.ok) {
    throw new Error(normalized.error.message);
  }
  const executions = new Map(
    config.executions.map(execution => [execution.id, execution]),
  );
  const filters = normalized.value.map(filter => ({
    ...filter,
    field: executions.get(filter.id)?.field ?? filter.id,
    facetable: config.filters.find(definition => definition.id === filter.id)!
      .facetable,
  }));
  const requestFingerprint = await computeFingerprint(
    JSON.stringify({
      mode: request.mode,
      environment: request.environment,
      query: normalizeSearchQuery(request.query),
      filters,
      facetId: request.facetId,
      limit,
      filterFingerprint: config.filterFingerprint,
    }),
  );

  return {
    environment: request.environment,
    query: normalizeSearchQuery(request.query),
    filters,
    facetId: request.facetId,
    limit,
    filterFingerprint: config.filterFingerprint,
    requestFingerprint,
  };
}
